import { Component, DoCheck, Inject, OnInit } from "@angular/core";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MatSnackBar } from "@angular/material/snack-bar";

@Component({
    selector: "app-create-group-dialog",
    templateUrl: "./create-group-dialog.component.html",
    styleUrls: ["./create-group-dialog.component.css"],
    standalone: false
})
export class CreateGroupDialogComponent implements OnInit, DoCheck {
  groupForm: FormGroup;
  groupPositions: string[] = [];
  subGroupDetails: {
    name: string;
    module: string;
    weight: number;
    position: string;
    modules: any[];
  }[] = [];
  modulesPool: any[] = [];
  groups: any[] = [];
  isEditMode: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<CreateGroupDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: FormBuilder,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    const existingGroups = Array.isArray(this.data?.existingGroups)
      ? this.data.existingGroups.length
      : 0;
    this.groupPositions = Array.from({ length: existingGroups + 1 }, (_, i) =>
      (i + 1).toString()
    );
    


    // Helper to create a group object from scratch or from existing data
    const buildGroup = (g?: any) => {
      console.log(`=== BUILDING GROUP ===`);
      console.log(`Input parameter g:`, g);
      console.log(`Is editing existing group:`, !!g);
      
      const groupForm = this.fb.group({
        groupName: [g?.groupName || "", Validators.required],
        groupPosition: [g?.groupPosition || this.groupPositions[0] || "1", Validators.required],
        createSubGroups: [g?.createSubGroups || false],
        numberOfSubGroups: [g?.numberOfSubGroups || 1, Validators.required],
      });

      let subGroupDetails: any[] = [];
      if (g?.createSubGroups && Array.isArray(g.subGroups)) {
        subGroupDetails = g.subGroups.map((sub: any, idx: number) => {
          
          return {
            name: sub.name || `Sub Group ${idx + 1}`,
            module: sub.module || "",
            weight: sub.weight || 0,
            position: sub.position || "",
            modules: (this.data.modules || []).map((mod: any) => {
              // Look for saved module by module_code (backend) or code (frontend)
              const savedMod = (sub.modules || []).find(
                (m: any) => m.module_code === mod.code || m.code === mod.code
              );
              
              // Debug logging for module mapping
              console.log(`Mapping module ${mod.name || mod.module_name}:`, {
                moduleCode: mod.code,
                backendModules: sub.modules,
                foundMatch: !!savedMod,
                savedModule: savedMod
              });
              
              return {
                ...mod,
                selected: !!savedMod,
                weight: savedMod ? savedMod.weight : 0,
                position: savedMod ? savedMod.position : mod.position || "",
                channel: savedMod ? savedMod.channel : mod.channel || "",
              };
            }),
          };
        });
        

      } else {
        // No subgroups, just modules
        subGroupDetails = [
          {
            name: "Sub Group 1",
            module: "",
            weight: 100, // Default weight for single subgroup
            position: this.groupPositions[0] || "1", // Default position
            modules: (this.data.modules || []).map((mod: any) => {
              // Look for saved module by module_code (backend) or code (frontend)
              const savedMod = (g?.modules || []).find(
                (m: any) => m.module_code === mod.code || m.code === mod.code
              );
              return {
                ...mod,
                selected: !!savedMod,
                weight: savedMod ? savedMod.weight : 0,
                position: savedMod ? savedMod.position : mod.position || "",
                channel: savedMod ? savedMod.channel : mod.channel || "",
              };
            }),
          },
        ];
      }

      // React to numberOfSubGroups changes
      groupForm.get("numberOfSubGroups")?.valueChanges.subscribe((val) => {
        if (!groupForm.get("createSubGroups")?.value) return;
        const count = Number(val) || 1;
        
        // Store existing subgroups to preserve their names
        const existingSubGroups = [...subGroupDetails];
        
        while (subGroupDetails.length < count) {
          const nextNum = subGroupDetails.length + 1;
          
          subGroupDetails.push({
            name: this.generateSmartSubGroupName(subGroupDetails, nextNum),
            module: "",
            weight: 0,
            position: "",
            modules: (this.data.modules || []).map((mod: any) => ({
              ...mod,
              selected: false,
              weight: 0,
              position: mod.position || "",
              channel: mod.channel || "",
            })),
          });
        }
        while (subGroupDetails.length > count) {
          subGroupDetails.pop();
        }
        
        // Redistribute weights after changing number of subgroups
        this.distributeWeightsEvenly(subGroupDetails);
      });

      // React to createSubGroups checkbox changes
      groupForm.get("createSubGroups")?.valueChanges.subscribe((checked) => {
        if (!checked) {
          // If unchecked, keep only the first subgroup
          // Preserve the name of the first subgroup if it has a custom name
          const firstSubGroup = subGroupDetails[0];
          // Clear the array and add the first subgroup back
          subGroupDetails.splice(0, subGroupDetails.length);
          subGroupDetails.push({
            ...firstSubGroup,
            name: firstSubGroup.name && firstSubGroup.name.trim() !== '' ? firstSubGroup.name : "Sub Group 1" // Preserve custom name or use default
          });
          groupForm.get("numberOfSubGroups")?.setValue(1);
        }
        // If checked, do nothing (user can add more)
      });

      console.log(`=== FINAL BUILT GROUP ===`);
      console.log(`Group form:`, groupForm.value);
      console.log(`Subgroup details:`, subGroupDetails.map(sub => ({
        name: sub.name,
        position: sub.position,
        weight: sub.weight
      })));
      console.log(`=== END BUILDING GROUP ===`);
      
      return { groupForm, subGroupDetails };
    };

    // If editing, initialize groups from the passed group(s)
    this.isEditMode = !!this.data?.group;
    if (this.data.group) {
      console.log("=== LOADING EXISTING GROUPS FOR EDITING ===");
      console.log("Raw group data:", this.data.group);
      console.log("=== MODULE DATA DEBUG ===");
      console.log("Available modules:", this.data.modules);
      console.log("Modules structure:", this.data.modules?.map((mod: any) => ({
        name: mod.name || mod.module_name,
        code: mod.code,
        module_code: mod.module_code
      })));
      console.log("=== END MODULE DATA DEBUG ===");
      console.log("Data structure analysis:");
      console.log("- Has group property:", !!this.data.group);
      console.log("- Group type:", typeof this.data.group);
      console.log("- Is array:", Array.isArray(this.data.group));
      console.log("- Group keys:", this.data.group ? Object.keys(this.data.group) : 'null');
      
      if (this.data.group.subGroups) {
        console.log("- Has subGroups:", true);
        console.log("- subGroups type:", typeof this.data.group.subGroups);
        console.log("- subGroups is array:", Array.isArray(this.data.group.subGroups));
        console.log("- subGroups length:", this.data.group.subGroups.length);
        console.log("- subGroups content:", this.data.group.subGroups.map((sub: any, idx: number) => ({
          index: idx,
          name: sub.name,
          position: sub.position,
          weight: sub.weight,
          hasModules: !!sub.modules,
          moduleCount: sub.modules ? sub.modules.length : 0,
          modules: sub.modules
        })));
      } else {
        console.log("- Has subGroups: false");
        console.log("- Available properties:", Object.keys(this.data.group));
      }
      
      const groupsToEdit = Array.isArray(this.data.group)
        ? this.data.group
        : [this.data.group];
      
      console.log("Groups to edit:", groupsToEdit);
      
      this.groups = groupsToEdit.map((g: any, idx: number) => {
        console.log(`Building group ${idx + 1}:`, g);
        const builtGroup = buildGroup(g);
        console.log(`Built group ${idx + 1}:`, {
          groupForm: builtGroup.groupForm.value,
          subGroupDetails: builtGroup.subGroupDetails.map(sub => ({
            name: sub.name,
            position: sub.position,
            weight: sub.weight
          }))
        });
        return builtGroup;
      });
      
      // Debug: Log the loaded groups to see what names are preserved
      console.log("=== DEBUG: Loaded groups for editing ===");
      this.groups.forEach((group, gIdx) => {
        if (group.subGroupDetails) {
          console.log(`Group ${gIdx + 1} subgroups:`, group.subGroupDetails.map(sub => ({
            name: sub.name,
            position: sub.position,
            weight: sub.weight
          })));
        }
      });
      console.log("=== END DEBUG ===");
    } else {
      // Not editing: initialize with one empty group
      console.log("=== CREATING NEW GROUPS (NOT EDITING) ===");
      const newGroup = buildGroup();
      console.log("New group created:", {
        groupForm: newGroup.groupForm.value,
        subGroupDetails: newGroup.subGroupDetails.map(sub => ({
          name: sub.name,
          position: sub.position,
          weight: sub.weight
        }))
      });
      this.groups = [newGroup];
      console.log("=== END CREATING NEW GROUPS ===");
    }

    console.log("=== UPDATING GROUP POSITIONS IN NGONINIT ===");
    this.updateGroupPositions();
    console.log("Group positions updated:", this.groupPositions);
    console.log("=== END UPDATING GROUP POSITIONS IN NGONINIT ===");
  }

  onCancel(): void {
    sessionStorage.removeItem("lastGroupConfig");
    this.dialogRef.close();
  }

  onSave(): void {
    console.log("=== onSave method called ===");
    console.log("Current groups array:", this.groups);
    
    // Log the current state of all subgroups before any processing
    console.log("=== SUBGROUP STATE BEFORE SAVE ===");
    this.groups.forEach((group, groupIdx) => {
      if (group.subGroupDetails) {
        group.subGroupDetails.forEach((sub, subIdx) => {
          console.log(`Group ${groupIdx + 1}, Subgroup ${subIdx + 1}: "${sub.name}"`);
        });
      }
    });
    console.log("=== END SUBGROUP STATE BEFORE SAVE ===");
    
    // Validate required fields
    for (const [gIdx, group] of this.groups.entries()) {
      console.log(`Validating Group ${gIdx + 1}:`, {
        groupForm: group.groupForm.value,
        subGroupDetails: group.subGroupDetails.map(sub => ({
          name: sub.name,
          position: sub.position,
          weight: sub.weight
        }))
      });
      if (group.groupForm.invalid) {
        group.groupForm.markAllAsTouched();
        this.snackBar.open(
          "Please fill all required fields in each group.",
          "Close",
          {
            duration: 3000,
            panelClass: "snackbar-error",
            horizontalPosition: "right",
            verticalPosition: "top",
          }
        );
        return;
      }
      
      // Validate subgroup weights if subgroups are enabled
      if (group.groupForm.value.createSubGroups) {
        const totalWeight = group.subGroupDetails.reduce((sum, sub) => sum + (sub.weight || 0), 0);
        if (totalWeight !== 100) {
          this.snackBar.open(
            `Total weight of subgroups in Group ${gIdx + 1} must equal 100. Current total: ${totalWeight}`,
            "Close",
            {
              duration: 3000,
              panelClass: "snackbar-error",
              horizontalPosition: "right",
              verticalPosition: "top",
            }
          );
          return;
        }
        
        // Validate subgroup names
        console.log(`=== VALIDATING SUBGROUP NAMES FOR GROUP ${gIdx + 1} ===`);
        for (const sub of group.subGroupDetails) {
          console.log(`Validating subgroup: "${sub.name}"`);
          if (!sub.name || sub.name.trim() === "") {
            console.error(`Subgroup name validation failed: empty or undefined name`);
            this.snackBar.open(
              `Please provide a name for all subgroups in Group ${gIdx + 1}`,
              "Close",
              {
                duration: 3000,
                panelClass: "snackbar-error",
                horizontalPosition: "right",
                verticalPosition: "top",
              }
            );
            return;
          }
          console.log(`Subgroup name validation passed: "${sub.name}"`);
          
          // Validate subgroup position
          if (!sub.position || sub.position.trim() === "") {
            this.snackBar.open(
              `Please select a position for subgroup "${sub.name}" in Group ${gIdx + 1}`,
              "Close",
              {
                duration: 3000,
                panelClass: "snackbar-error",
                horizontalPosition: "right",
                verticalPosition: "top",
              }
            );
            return;
          }
          
          // Validate subgroup weight
          if (sub.weight === null || sub.weight === undefined || sub.weight < 0) {
            this.snackBar.open(
              `Please set a valid weight for subgroup "${sub.name}" in Group ${gIdx + 1}`,
              "Close",
              {
                duration: 3000,
                panelClass: "snackbar-error",
                horizontalPosition: "right",
                verticalPosition: "top",
              }
            );
            return;
          }
        }
        console.log(`=== END VALIDATING SUBGROUP NAMES ===`);
      }
      
      // Validate at least one module selected in each subgroup if needed
      if (group.groupForm.value.createSubGroups) {
        for (const sub of group.subGroupDetails) {
          if (!sub.modules.some((m: any) => m.selected)) {
            this.snackBar.open(
              `Please select at least one module in ${sub.name} of Group ${
                gIdx + 1
              }.`,
              "Close",
              {
                duration: 3000,
                panelClass: "snackbar-error",
                horizontalPosition: "right",
                verticalPosition: "top",
              }
            );
            return;
          }
        }
      } else {
        if (
          !group.subGroupDetails[0] ||
          !group.subGroupDetails[0].modules.some((m: any) => m.selected)
        ) {
          this.snackBar.open(
            `Please select at least one module in Group ${gIdx + 1}.`,
            "Close",
            {
              duration: 3000,
              panelClass: "snackbar-error",
              horizontalPosition: "right",
              verticalPosition: "top",
            }
          );
          return;
        }
      }
      
      // Validate that total weight of selected modules equals 100
      if (group.groupForm.value.createSubGroups) {
        // Validate modules in each subgroup
        for (const sub of group.subGroupDetails) {
          // Validate that total weight of selected modules equals 100
          const totalModuleWeight = this.getTotalModuleWeight(sub.modules);
          if (totalModuleWeight !== 100) {
            this.snackBar.open(
              `Total weight of selected modules in ${sub.name} of Group ${gIdx + 1} must equal 100. Current total: ${totalModuleWeight}`,
              "Close",
              {
                duration: 4000,
                panelClass: "snackbar-error",
                horizontalPosition: "right",
                verticalPosition: "top",
              }
            );
            return;
          }
          
          // Validate that all selected modules have position assigned
          const selectedModulesWithoutPosition = sub.modules.filter(
            (m: any) => m.selected && (m.position === null || m.position === undefined || m.position === "")
          );
          
          if (selectedModulesWithoutPosition.length > 0) {
            const moduleNames = selectedModulesWithoutPosition.map((m: any) => this.getModuleDisplayName(m) || "Unknown").join(", ");
            this.snackBar.open(
              `Please select position for all selected modules in ${sub.name} of Group ${gIdx + 1}. Missing position for: ${moduleNames}`,
              "Close",
              {
                duration: 4000,
                panelClass: "snackbar-error",
                horizontalPosition: "right",
                verticalPosition: "top",
              }
            );
            return;
          }
        }
      } else {
        // Validate modules in the single subgroup (when subgroups are not enabled)
        if (group.subGroupDetails[0] && group.subGroupDetails[0].modules) {
          // Validate that total weight of selected modules equals 100
          const totalModuleWeight = this.getTotalModuleWeight(group.subGroupDetails[0].modules);
          if (totalModuleWeight !== 100) {
            this.snackBar.open(
              `Total weight of selected modules in Group ${gIdx + 1} must equal 100. Current total: ${totalModuleWeight}`,
              "Close",
              {
                duration: 4000,
                panelClass: "snackbar-error",
                horizontalPosition: "right",
                verticalPosition: "top",
              }
            );
            return;
          }
          
          // Validate that all selected modules have position assigned
          const selectedModulesWithoutPosition = group.subGroupDetails[0].modules.filter(
            (m: any) => m.selected && (m.position === null || m.position === undefined || m.position === "")
          );
          
          if (selectedModulesWithoutPosition.length > 0) {
            const moduleNames = selectedModulesWithoutPosition.map((m: any) => this.getModuleDisplayName(m) || "Unknown").join(", ");
            this.snackBar.open(
              `Please select position for all selected modules in Group ${gIdx + 1}. Missing position for: ${moduleNames}`,
              "Close",
              {
                duration: 4000,
                panelClass: "snackbar-error",
                horizontalPosition: "right",
                verticalPosition: "top",
              }
            );
            return;
          }
        }
      }
    }

    // Collect all group data
    console.log("=== PREPARING PAYLOAD DATA ===");
    console.log("Raw groups data before mapping:", this.groups);
    
    const allGroupsData = this.groups.map((group, groupIndex) => {
      console.log(`Processing Group ${groupIndex + 1}:`, {
        groupFormValue: group.groupForm.value,
        subGroupDetails: group.subGroupDetails.map((sub, subIdx) => ({
          index: subIdx,
          name: sub.name,
          position: sub.position,
          weight: sub.weight
        }))
      });
      
      const mappedGroup = {
        ...group.groupForm.value,
        subGroups: group.groupForm.value.createSubGroups
          ? group.subGroupDetails.map((sub, subIdx) => {
              console.log(`Mapping subgroup ${subIdx + 1}: "${sub.name}"`);
              return {
                ...sub,
                modules: sub.modules.filter((m: any) => m.selected),
              };
            })
          : undefined,
        modules: !group.groupForm.value.createSubGroups
          ? group.subGroupDetails[0] && group.subGroupDetails[0].modules
            ? group.subGroupDetails[0].modules.filter((m: any) => m.selected)
            : []
          : undefined,
      };
      
      console.log(`Mapped Group ${groupIndex + 1}:`, mappedGroup);
      return mappedGroup;
    });
    
    // Debug: Log subgroup names to see what's being captured
    console.log("=== DEBUG: Subgroup names before saving ===");
    allGroupsData.forEach((group, gIdx) => {
      if (group.subGroups) {
        console.log(`Group ${gIdx + 1} subgroups:`, group.subGroups.map(sub => ({
          name: sub.name,
          position: sub.position,
          weight: sub.weight
        })));
      }
    });
    console.log("=== END DEBUG ===");
    
    // Also log the raw subGroupDetails to see what's in the component
    console.log("=== DEBUG: Raw subGroupDetails in component ===");
    this.groups.forEach((group, gIdx) => {
      if (group.subGroupDetails) {
        console.log(`Group ${gIdx + 1} raw subgroups:`, group.subGroupDetails.map(sub => ({
          name: sub.name,
          position: sub.position,
          weight: sub.weight
        })));
      }
    });
    console.log("=== END RAW DEBUG ===");
    
    // Additional debugging: Check if there's a mismatch between component state and final data
    console.log("=== DEBUG: Checking for name mismatches ===");
    this.groups.forEach((group, gIdx) => {
      if (group.subGroupDetails) {
        group.subGroupDetails.forEach((sub, subIdx) => {
          const finalData = allGroupsData[gIdx]?.subGroups?.[subIdx];
          if (finalData && sub.name !== finalData.name) {
            console.warn(`NAME MISMATCH in Group ${gIdx + 1}, Subgroup ${subIdx + 1}:`);
            console.warn(`  Component state: "${sub.name}"`);
            console.warn(`  Final data: "${finalData.name}"`);
          }
        });
      }
    });
    console.log("=== END MISMATCH DEBUG ===");
    
    sessionStorage.setItem("lastGroupConfig", JSON.stringify(allGroupsData));
    this.dialogRef.close(allGroupsData);
  }

  onAddModule(): void {
    if (this.subGroupDetails.length > 0) {
      this.subGroupDetails[0].modules.push({
        name: "",
        code: "",
        weight: 0,
        position: "",
        selected: false,
      });
    }
  }

  addSubGroup(gIdx: number) {
    const group = this.groups[gIdx];
    const nextNum = group.subGroupDetails.length + 1;
    
    // Find an available position that's not already used
    const usedPositions = group.subGroupDetails.map(sub => sub.position);
    const availablePosition = this.groupPositions.find(pos => !usedPositions.includes(pos)) || this.groupPositions[0] || "1";
    
    const newSubGroup = {
      name: this.generateSmartSubGroupName(group.subGroupDetails, nextNum),
      module: "",
      weight: 0,
      position: availablePosition,
      modules: (this.data.modules || []).map((mod: any) => ({
        ...mod,
        selected: false,
        weight: 0,
        position: mod.position || "",
        channel: mod.channel || "",
      })),
    };
    
    group.subGroupDetails.push(newSubGroup);
    group.groupForm.get("numberOfSubGroups")?.setValue(nextNum);
    
    // Auto-distribute weights
    this.distributeWeightsEvenly(group.subGroupDetails);
    
    // Debug: Log the newly added subgroup
    console.log(`Added new subgroup: Group ${gIdx + 1}, Subgroup ${nextNum}:`, {
      name: newSubGroup.name,
      position: newSubGroup.position,
      weight: newSubGroup.weight
    });
    
    // Debug: Log all subgroups in this group after addition
    console.log(`All subgroups in Group ${gIdx + 1} after addition:`, 
      group.subGroupDetails.map(sub => ({
        name: sub.name,
        position: sub.position,
        weight: sub.weight
      }))
    );
    
    // Trigger change detection to ensure the UI updates
    setTimeout(() => {
      // Focus on the name input for the newly added subgroup
      const nameInput = document.querySelector(`input[placeholder="Enter sub group name"]`) as HTMLInputElement;
      if (nameInput) {
        nameInput.focus();
        nameInput.select();
      }
    }, 100);
  }
  
  distributeWeightsEvenly(subGroups: any[]) {
    if (subGroups.length === 0) return;
    
    const weightPerGroup = Math.floor(100 / subGroups.length);
    const remainder = 100 % subGroups.length;
    
    console.log(`Distributing weights evenly among ${subGroups.length} subgroups:`, {
      weightPerGroup,
      remainder,
      subgroups: subGroups.map(sub => ({ name: sub.name, weight: sub.weight }))
    });
    
    subGroups.forEach((sub, index) => {
      if (index < remainder) {
        sub.weight = weightPerGroup + 1;
      } else {
        sub.weight = weightPerGroup;
      }
    });
    
    console.log(`Weights distributed:`, subGroups.map(sub => ({ name: sub.name, weight: sub.weight })));
  }

  addModuleToSubGroup(gIdx: number, subIdx: number) {
    const group = this.groups[gIdx];
    const subGroup = group.subGroupDetails[subIdx];
    subGroup.modules.push({
      name: "",
      code: "",
      selected: false,
      weight: 0,
      position: this.groupPositions[0] || "",
      channel: "",
    });
    this.updateGroupPositions();
  }

  updateGroupPositions() {
    console.log("=== UPDATE GROUP POSITIONS METHOD ===");
    console.log("Data existingGroups:", this.data?.existingGroups);
    
    const existingGroups = Array.isArray(this.data?.existingGroups)
      ? this.data.existingGroups.length
      : 0;
    
    console.log("Existing groups count:", existingGroups);
    
    this.groupPositions = Array.from({ length: existingGroups + 1 }, (_, i) =>
      (i + 1).toString()
    );
    
    console.log("Generated group positions:", this.groupPositions);
    console.log("=== END UPDATE GROUP POSITIONS METHOD ===");
  }

  // Every subgroup lists every module, and the template asks for positions and
  // duplicates per module row. Recomputing that per row was quadratic in the
  // module count, so it is computed once per subgroup per change detection.
  private statsGeneration = 0;
  private subGroupStats = new WeakMap<any, {
    generation: number;
    count: number;
    positions: string[];
    validPositions: Set<string>;
    positionCounts: Map<string, number>;
  }>();

  ngDoCheck(): void {
    this.statsGeneration++;
  }

  private getSubGroupStats(sub: any) {
    const cached = this.subGroupStats.get(sub);
    if (cached && cached.generation === this.statsGeneration) {
      return cached;
    }

    let count = 0;
    const positionCounts = new Map<string, number>();
    for (const m of sub.modules || []) {
      if (!m.selected) continue;
      count++;
      if (m.position) {
        const key = String(m.position).trim();
        positionCounts.set(key, (positionCounts.get(key) || 0) + 1);
      }
    }

    // Reuse the same array while the count is unchanged so the option lists
    // in the template are not rebuilt
    const reuse = cached && cached.count === count;
    const positions = reuse
      ? cached!.positions
      : Array.from({ length: count }, (_, i) => (i + 1).toString());
    const stats = {
      generation: this.statsGeneration,
      count,
      positions,
      validPositions: reuse ? cached!.validPositions : new Set(positions),
      positionCounts,
    };
    this.subGroupStats.set(sub, stats);
    return stats;
  }

  // Subgroup module tables are paged: drawing every module of a large site in
  // every subgroup made the dialog slow to render and to update. View state is
  // kept off the subgroup objects so it never reaches the saved result.
  readonly modulePageSize = 25;
  private moduleViews = new WeakMap<any, {
    page: number;
    search: string;
    selectedOnly: boolean;
    generation: number;
    order: any[] | null;
    filtered: any[];
    paged: any[];
  }>();

  getModuleView(sub: any) {
    let view = this.moduleViews.get(sub);
    if (!view) {
      view = { page: 0, search: "", selectedOnly: false, generation: -1, order: null, filtered: [], paged: [] };
      this.moduleViews.set(sub, view);
    }
    if (view.generation !== this.statsGeneration) {
      const modules: any[] = sub.modules || [];
      // Selected modules are listed first so they are on page 1. The order is a
      // snapshot (see setModulePage/setModuleFilter): re-sorting on every tick
      // would move the row away from the user and shift the other pages.
      if (!view.order || view.order.length !== modules.length) {
        view.order = [
          ...modules.filter((m: any) => m.selected),
          ...modules.filter((m: any) => !m.selected),
        ];
      }
      const term = view.search.trim().toLowerCase();
      view.filtered = view.order.filter((m: any) => {
        if (view!.selectedOnly && !m.selected) return false;
        if (!term) return true;
        return [this.getModuleDisplayName(m), m.code, m.module_code]
          .some((v) => v && String(v).toLowerCase().includes(term));
      });
      const lastPage = Math.max(0, Math.ceil(view.filtered.length / this.modulePageSize) - 1);
      view.page = Math.min(view.page, lastPage);
      const start = view.page * this.modulePageSize;
      const paged = view.filtered.slice(start, start + this.modulePageSize);
      // Keep the previous array when the rows are the same, so the table is not re-diffed
      const same =
        paged.length === view.paged.length && paged.every((m, i) => m === view!.paged[i]);
      if (!same) view.paged = paged;
      view.generation = this.statsGeneration;
    }
    return view;
  }

  getModulePageCount(sub: any): number {
    return Math.max(1, Math.ceil(this.getModuleView(sub).filtered.length / this.modulePageSize));
  }

  setModulePage(sub: any, page: number): void {
    const view = this.getModuleView(sub);
    view.page = Math.max(0, Math.min(page, this.getModulePageCount(sub) - 1));
    // Back on page 1, bring newly selected modules to the top
    if (view.page === 0) view.order = null;
    view.generation = -1;
  }

  setModuleFilter(sub: any, changes: { search?: string; selectedOnly?: boolean }): void {
    const view = this.getModuleView(sub);
    Object.assign(view, changes);
    view.page = 0;
    view.order = null;
    view.generation = -1;
  }

  getSelectedModuleCount(sub: any): number {
    return this.getSubGroupStats(sub).count;
  }

  /** Selected modules with a bad weight or position, which may be on another page. */
  getInvalidSelectedCount(sub: any): number {
    return (sub.modules || []).filter(
      (m: any) =>
        m.selected && (this.isModuleWeightInvalid(m) || this.isModulePositionInvalid(m, sub))
    ).length;
  }

  getPositionsForSubGroup(sub: any, excludeModule?: any): string[] {
    // Always show all positions from 1 to the number of selected modules
    // (empty when none are selected, so only the placeholder shows)
    return this.getSubGroupStats(sub).positions;
  }
  
  getTotalSubGroupWeight(subGroups: any[]): number {
    return subGroups.reduce((sum, sub) => sum + (sub.weight || 0), 0);
  }
  
  getWeightValidationMessage(subGroups: any[]): string {
    const totalWeight = this.getTotalSubGroupWeight(subGroups);
    if (totalWeight === 0) {
      return "Please set weights for subgroups";
    } else if (totalWeight < 100) {
      return `Total weight: ${totalWeight}/100 (${100 - totalWeight} remaining)`;
    } else if (totalWeight > 100) {
      return `Total weight: ${totalWeight}/100 (${totalWeight - 100} over)`;
    } else {
      return "Weight validated ✓";
    }
  }
  
  isWeightInvalid(subGroups: any[], currentSub: any): boolean {
    const currentWeight = currentSub.weight || 0;
    const otherSubsWeight = subGroups
      .filter(sub => sub !== currentSub)
      .reduce((sum, sub) => sum + (sub.weight || 0), 0);
    const totalWeight = currentWeight + otherSubsWeight;
    
    return currentWeight < 0 || currentWeight > 100 || totalWeight > 100;
  }
  
  isModuleWeightInvalid(module: any): boolean {
    // Check if module is selected but has an invalid weight value
    if (!module.selected) {
      return false; // Not selected, so no validation needed
    }
    // Module is selected - weight can be 0 or any positive number, but not null/undefined/empty/negative
    const weight = module.weight;
    // Allow 0 and positive numbers, only mark invalid for null/undefined/empty string/negative
    if (weight === null || weight === undefined || weight === "") {
      return true;
    }
    // Check for negative values
    if (Number(weight) < 0) {
      return true;
    }
    return false;
  }
  
  isModulePositionInvalid(module: any, subGroup?: any): boolean {
    // Check if module is selected but doesn't have a valid position
    if (!module.selected) {
      return false; // Not selected, so no validation needed
    }
    
    // Module is selected, so it must have a valid position
    const position = module.position;
    
    // Check for empty/null/undefined values - be very strict
    if (position === null || 
        position === undefined || 
        position === "" || 
        position === "null" || 
        position === 0 ||
        (typeof position === 'string' && position.trim() === "")) {
      return true; // Invalid: no position selected
    }
    
    // If subgroup is provided, validate that the position is in the valid list
    if (subGroup) {
      const stats = this.getSubGroupStats(subGroup);
      const positionStr = String(position).trim();
      if (stats.count > 0 && !stats.validPositions.has(positionStr)) {
        return true; // Invalid: position not in valid list or already taken
      }

      // Additional check: ensure position is not duplicate. This module is
      // selected with a position, so it is counted once itself.
      if ((stats.positionCounts.get(positionStr) || 0) > 1) {
        return true; // Invalid: duplicate position
      }
      
      // If no valid positions available, it means no modules are selected, so validation doesn't apply
    }
    
    return false; // Valid position
  }

  onModulePositionChange(module: any, subGroup: any): void {
    // When a position is changed, check if it's a duplicate
    if (!module.selected || !module.position) {
      return;
    }
    
    const positionStr = String(module.position).trim();
    
    // Find if any other module in the same subgroup has this position
    const duplicateModule = subGroup.modules?.find((m: any) => 
      m.selected && 
      m !== module && 
      m.position && 
      String(m.position).trim() === positionStr
    );
    
    // If duplicate found, clear the position and show error
    if (duplicateModule) {
      // Clear the duplicate position
      module.position = "";
      
      // Show error notification
      this.snackBar.open(
        `Position ${positionStr} is already assigned to "${this.getModuleDisplayName(duplicateModule)}". Please select a different position.`,
        'Close',
        {
          duration: 4000,
          horizontalPosition: 'right',
          verticalPosition: 'top',
          panelClass: ['error-snackbar']
        }
      );
    }
  }

  isFormValid(): boolean {
    // Also called from onSave(), so never trust stats from an earlier pass
    this.statsGeneration++;

    // Check all groups
    for (const [gIdx, group] of this.groups.entries()) {
      // Validate required fields in group form
      if (group.groupForm.invalid) {
        return false;
      }
      
      // Validate subgroup weights if subgroups are enabled
      if (group.groupForm.value.createSubGroups) {
        const totalWeight = group.subGroupDetails.reduce((sum, sub) => sum + (sub.weight || 0), 0);
        if (totalWeight !== 100) {
          return false;
        }
        
        // Validate subgroup names and positions
        for (const sub of group.subGroupDetails) {
          if (!sub.name || sub.name.trim() === "") {
            return false;
          }
          
          if (!sub.position || sub.position.trim() === "") {
            return false;
          }
          
          if (sub.weight === null || sub.weight === undefined || sub.weight < 0) {
            return false;
          }
        }
        
        // Validate modules in each subgroup
        for (const sub of group.subGroupDetails) {
          // Check if at least one module is selected
          if (!sub.modules.some((m: any) => m.selected)) {
            return false;
          }
          
          // Check if total weight of selected modules equals 100
          const totalModuleWeight = this.getTotalModuleWeight(sub.modules);
          if (totalModuleWeight !== 100) {
            return false;
          }
          
          // Check if all selected modules have position assigned
          const selectedModulesWithoutPosition = sub.modules.filter(
            (m: any) => this.isModulePositionInvalid(m, sub)
          );
          if (selectedModulesWithoutPosition.length > 0) {
            return false;
          }
        }
      } else {
        // Validate modules in the single subgroup (when subgroups are not enabled)
        if (group.subGroupDetails[0] && group.subGroupDetails[0].modules) {
          // Check if at least one module is selected
          if (!group.subGroupDetails[0].modules.some((m: any) => m.selected)) {
            return false;
          }
          
          // Check if total weight of selected modules equals 100
          const totalModuleWeight = this.getTotalModuleWeight(group.subGroupDetails[0].modules);
          if (totalModuleWeight !== 100) {
            return false;
          }
          
          // Check if all selected modules have position assigned
          const selectedModulesWithoutPosition = group.subGroupDetails[0].modules.filter(
            (m: any) => this.isModulePositionInvalid(m, group.subGroupDetails[0])
          );
          if (selectedModulesWithoutPosition.length > 0) {
            return false;
          }
        } else {
          return false;
        }
      }
    }
    
    return true; // All validations passed
  }
  
  getTotalModuleWeight(modules: any[]): number {
    return modules
      .filter((m: any) => m.selected)
      .reduce((sum, m: any) => sum + (Number(m.weight) || 0), 0);
  }

  /**
   * Helper function to get the display name for a module (alias if available, otherwise module name)
   */
  getModuleDisplayName(module: any): string {
    if (!module) return "";
    // Check for alias in various possible field names
    const alias = module.aliasName || module.module_alias || module.alias;
    if (alias && alias.trim() !== "") {
      return alias;
    }
    // Fallback to module name
    return module.name || module.module_name || "";
  }
  createGroup() {
    console.log("=== CREATING NEW GROUP ===");
    
    const groupForm = this.fb.group({
      groupName: ["", Validators.required],
      groupPosition: ["", Validators.required],
      createSubGroups: [false],
      numberOfSubGroups: [1, Validators.required],
    });
    
    const subGroupDetails = [
      {
        name: "Sub Group 1", // This will be the default name for the first subgroup
        module: "",
        weight: 100, // Default weight for single subgroup
        position: this.groupPositions[0] || "1", // Default position
        modules: (this.data.modules || []).map((mod: any) => ({
          ...mod,
          selected: false,
          weight: 0,
          position: mod.position || "",
          channel: mod.channel || "",
        })),
      },
    ];
    
    console.log("Initial subgroup details:", subGroupDetails.map(sub => ({ name: sub.name, weight: sub.weight })));
    // React to numberOfSubGroups changes
    groupForm.get("numberOfSubGroups")?.valueChanges.subscribe((val) => {
      console.log(`=== NUMBER OF SUBGROUPS CHANGED ===`);
      console.log(`New value: ${val}`);
      console.log(`Create subgroups enabled: ${groupForm.get("createSubGroups")?.value}`);
      
      if (!groupForm.get("createSubGroups")?.value) {
        console.log(`Create subgroups not enabled, returning early`);
        return;
      }
      
      const count = Number(val) || 1;
      console.log(`Target count: ${count}`);
      console.log(`Current subgroups before change:`, subGroupDetails.map(sub => ({ name: sub.name, weight: sub.weight })));
      
      // Store existing subgroups to preserve their names
      const existingSubGroups = [...subGroupDetails];
      console.log(`Existing subgroups to preserve:`, existingSubGroups.map(sub => ({ name: sub.name, weight: sub.weight })));
      
      while (subGroupDetails.length < count) {
        const nextNum = subGroupDetails.length + 1;
        const newName = this.generateSmartSubGroupName(subGroupDetails, nextNum);
        console.log(`Adding subgroup ${nextNum} with name: "${newName}"`);
        
        subGroupDetails.push({
          name: newName,
          module: "",
          weight: 0,
          position: "",
          modules: (this.data.modules || []).map((mod: any) => ({
            ...mod,
            selected: false,
            weight: 0,
            position: mod.position || "",
            channel: mod.channel || "",
          })),
        });
      }
      while (subGroupDetails.length > count) {
        const removed = subGroupDetails.pop();
        console.log(`Removed subgroup:`, removed);
      }
      
      console.log(`Subgroups after change:`, subGroupDetails.map(sub => ({ name: sub.name, weight: sub.weight })));
      console.log(`=== END NUMBER OF SUBGROUPS CHANGE ===`);
    });
    
                     // React to createSubGroups checkbox changes
       groupForm.get("createSubGroups")?.valueChanges.subscribe((checked) => {
         console.log(`=== CREATE SUBGROUPS CHECKBOX CHANGED ===`);
         console.log(`Checkbox checked: ${checked}`);
         console.log(`Current subgroups before change:`, subGroupDetails.map(sub => ({ name: sub.name, weight: sub.weight })));
         
         if (!checked) {
           // If unchecked, keep only the first subgroup
           // Preserve the name of the first subgroup if it has a custom name
           const firstSubGroup = subGroupDetails[0];
           console.log(`First subgroup before preserving:`, firstSubGroup);
           
           // Clear the array and add the first subgroup back
           subGroupDetails.splice(0, subGroupDetails.length);
           subGroupDetails.push({
             ...firstSubGroup,
             name: firstSubGroup.name && firstSubGroup.name.trim() !== '' ? firstSubGroup.name : "Sub Group 1" // Preserve custom name or use default
           });
           
           console.log(`First subgroup after preserving:`, subGroupDetails[0]);
           groupForm.get("numberOfSubGroups")?.setValue(1);
         } else {
           console.log(`Checkbox checked - no changes to subgroups`);
         }
         
         console.log(`Subgroups after checkbox change:`, subGroupDetails.map(sub => ({ name: sub.name, weight: sub.weight })));
         console.log(`=== END CREATE SUBGROUPS CHECKBOX CHANGE ===`);
       });
    return { groupForm, subGroupDetails };
  }
  addGroup() {
    console.log("=== ADDING NEW GROUP ===");
    const newGroup = this.createGroup();
    console.log("New group created:", {
      groupForm: newGroup.groupForm.value,
      subGroupDetails: newGroup.subGroupDetails.map(sub => ({ name: sub.name, weight: sub.weight }))
    });
    
    this.groups.push(newGroup);
    console.log(`Total groups after adding: ${this.groups.length}`);
    console.log("=== END ADDING NEW GROUP ===");
  }

  deleteGroup(gIdx: number) {
    if (this.groups.length > 1) {
      this.groups.splice(gIdx, 1);
    }
  }

  deleteSubGroup(gIdx: number, subIdx: number) {
    const group = this.groups[gIdx];
    if (group.subGroupDetails.length > 1) {
      // Debug: Log the subgroup being deleted
      const deletedSubGroup = group.subGroupDetails[subIdx];
      console.log(`Deleting subgroup: Group ${gIdx + 1}, Subgroup ${subIdx + 1}:`, {
        name: deletedSubGroup.name,
        position: deletedSubGroup.position,
        weight: deletedSubGroup.weight
      });
      
      group.subGroupDetails.splice(subIdx, 1);
      group.groupForm
        .get("numberOfSubGroups")
        ?.setValue(group.subGroupDetails.length);
      
      // Don't renumber subgroup names - preserve user's custom names
      // Only set default names for subgroups that don't have custom names
      group.subGroupDetails.forEach((sub: any, idx: number) => {
        if (!sub.name || sub.name.trim() === '') {
          // If no name, use default
          sub.name = `Sub Group ${idx + 1}`;
        } else if (sub.name.startsWith('Sub Group ')) {
          // If it's a default name, update the number
          sub.name = `Sub Group ${idx + 1}`;
        }
        // If it's a custom name, keep it as is
      });
      
      // Debug: Log all subgroups after deletion
      console.log(`All subgroups in Group ${gIdx + 1} after deletion:`, 
        group.subGroupDetails.map(sub => ({
          name: sub.name,
          position: sub.position,
          weight: sub.weight
        }))
      );
      
      // Redistribute weights after deletion
      this.distributeWeightsEvenly(group.subGroupDetails);
    }
  }
  
  // Method to handle subgroup name changes
  onSubGroupNameChange(groupIndex: number, subGroupIndex: number, newName: string) {
    console.log(`=== SUBGROUP NAME CHANGE START ===`);
    console.log(`onSubGroupNameChange called: Group ${groupIndex}, Subgroup ${subGroupIndex}, New name: "${newName}"`);
    
    if (this.groups[groupIndex] && this.groups[groupIndex].subGroupDetails[subGroupIndex]) {
      const oldName = this.groups[groupIndex].subGroupDetails[subGroupIndex].name;
      console.log(`Old name: "${oldName}"`);
      
      // Update the name
      this.groups[groupIndex].subGroupDetails[subGroupIndex].name = newName;
      console.log(`Name updated in component state: "${newName}"`);
      
      // Debug: Log the current state of all subgroups in this group
      console.log(`Current subgroups in Group ${groupIndex + 1}:`, 
        this.groups[groupIndex].subGroupDetails.map(sub => ({
          name: sub.name,
          position: sub.position,
          weight: sub.weight
        }))
      );
      
      // Force change detection to ensure the UI updates
      setTimeout(() => {
        console.log(`=== SUBGROUP NAME CHANGE VERIFICATION ===`);
        console.log(`Subgroup name updated in UI: "${newName}"`);
        
        // Verify the name was actually updated in the component state
        const currentName = this.groups[groupIndex].subGroupDetails[subGroupIndex].name;
        console.log(`Verification - Current name in component state: "${currentName}"`);
        
        // Also verify the name is still correct in the array
        const allSubgroups = this.groups[groupIndex].subGroupDetails;
        console.log(`All subgroups in this group:`, allSubgroups.map((sub, idx) => ({
          index: idx,
          name: sub.name,
          isTarget: idx === subGroupIndex
        })));
        
        console.log(`=== SUBGROUP NAME CHANGE END ===`);
      }, 0);
    } else {
      console.error(`Could not update subgroup name: Group ${groupIndex}, Subgroup ${subGroupIndex} not found`);
      console.error(`Groups array:`, this.groups);
      console.error(`Group at index ${groupIndex}:`, this.groups[groupIndex]);
    }
  }
  
  // Debug method to log current state of all groups and subgroups
  debugCurrentState() {
    console.log("=== DEBUG: Current state of all groups ===");
    this.groups.forEach((group, gIdx) => {
      console.log(`Group ${gIdx + 1}:`, {
        name: group.groupForm.value.groupName,
        position: group.groupForm.value.groupPosition,
        createSubGroups: group.groupForm.value.createSubGroups,
        numberOfSubGroups: group.groupForm.value.numberOfSubGroups,
        subgroups: group.subGroupDetails.map((sub, subIdx) => ({
          index: subIdx,
          name: sub.name,
          position: sub.position,
          weight: sub.weight,
          moduleCount: sub.modules?.length || 0
        }))
      });
    });
    console.log("=== END DEBUG ===");
    
    // Also check for any potential issues
    this.checkForSubGroupNameIssues();
    this.verifyFormBinding();
  }
  
  // Method to check for potential subgroup name issues
  private checkForSubGroupNameIssues() {
    console.log("=== CHECKING FOR SUBGROUP NAME ISSUES ===");
    
    this.groups.forEach((group, groupIdx) => {
      if (group.subGroupDetails) {
        group.subGroupDetails.forEach((sub, subIdx) => {
          // Check if name is empty or undefined
          if (!sub.name || sub.name.trim() === '') {
            console.warn(`⚠️ ISSUE: Empty subgroup name in Group ${groupIdx + 1}, Subgroup ${subIdx + 1}`);
          }
          
          // Check if name is the default pattern when it shouldn't be
          if (sub.name && sub.name.startsWith('Sub Group ') && subIdx > 0) {
            console.warn(`⚠️ ISSUE: Default name pattern in non-first subgroup: Group ${groupIdx + 1}, Subgroup ${subIdx + 1} -> "${sub.name}"`);
          }
          
          // Check if name contains suspicious patterns
          if (sub.name && sub.name.includes('undefined') || sub.name.includes('null')) {
            console.error(`🚨 CRITICAL: Subgroup name contains invalid value: Group ${groupIdx + 1}, Subgroup ${subIdx + 1} -> "${sub.name}"`);
          }
        });
      }
    });
    
    console.log("=== END SUBGROUP NAME ISSUES CHECK ===");
  }
  
  // Helper method to generate a smart name for new subgroups based on existing names
  private generateSmartSubGroupName(existingSubGroups: any[], nextNum: number): string {
    if (existingSubGroups.length === 0) {
      console.log(`No existing subgroups, using default name: Sub Group ${nextNum}`);
      return `Sub Group ${nextNum}`;
    }
    
    // Look for custom names (not starting with "Sub Group")
    const customNames = existingSubGroups
      .map(sub => sub.name)
      .filter(name => name && name.trim() !== '' && !name.startsWith('Sub Group '));
    
    console.log(`Existing subgroups:`, existingSubGroups.map(sub => sub.name));
    console.log(`Custom names found:`, customNames);
    
    if (customNames.length > 0) {
      // Use the first custom name as a base
      const baseName = customNames[0].replace(/\s*\d+$/, ''); // Remove any trailing numbers
      const newName = `${baseName} ${nextNum}`;
      console.log(`Using custom name base "${baseName}" for new subgroup: "${newName}"`);
      return newName;
    }
    
    // If no custom names, use default pattern
    console.log(`No custom names found, using default name: Sub Group ${nextNum}`);
    return `Sub Group ${nextNum}`;
  }
  

  
  // Method to verify form binding and component state consistency
  private verifyFormBinding() {
    console.log("=== VERIFYING FORM BINDING ===");
    
    this.groups.forEach((group, groupIdx) => {
      if (group.subGroupDetails) {
        group.subGroupDetails.forEach((sub, subIdx) => {
          // Check if the name in the component state matches what should be displayed
          console.log(`Group ${groupIdx + 1}, Subgroup ${subIdx + 1}:`, {
            componentStateName: sub.name,
            hasName: !!sub.name,
            nameLength: sub.name ? sub.name.length : 0,
            isDefaultPattern: sub.name && sub.name.startsWith('Sub Group '),
            position: sub.position,
            weight: sub.weight
          });
        });
      }
    });
    
    console.log("=== END FORM BINDING VERIFICATION ===");
  }
  
  // Test method to manually change a subgroup name (for debugging)
  testSubGroupNameChange(groupIndex: number, subGroupIndex: number, testName: string) {
    console.log(`=== TESTING SUBGROUP NAME CHANGE ===`);
    console.log(`Testing change: Group ${groupIndex}, Subgroup ${subGroupIndex} -> "${testName}"`);
    
    // Call the actual method
    this.onSubGroupNameChange(groupIndex, subGroupIndex, testName);
    
    // Verify the change
    setTimeout(() => {
      const currentName = this.groups[groupIndex]?.subGroupDetails[subGroupIndex]?.name;
      console.log(`Test result - Current name: "${currentName}"`);
      console.log(`Test successful: ${currentName === testName}`);
      console.log(`=== END TESTING ===`);
    }, 100);
  }
}
